import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, TextField, IconButton, CircularProgress, Paper, Typography, Grid, Avatar, Button, Dialog, DialogTitle, DialogContent, TextareaAutosize, Menu, MenuItem } from "@mui/material";
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import CallMadeOutlinedIcon from '@mui/icons-material/CallMadeOutlined';
import './chat.css';
import { req } from "./ServerUtils";
import { useParams, useNavigate } from "react-router-dom";
import { LinkedinPostPreview } from "./LinkedinPostPreview";
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import { ChatNavbar } from "./ChatNavbar";

export default function ChatBox() {
  const navigate = useNavigate();
  const { convId } = useParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const controllerRef = useRef(null);
  const chatBoxRef = useRef(null);
  const [responseId, setResponseId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [carousel, setCarousel] = useState(null);
  const [post, setPost] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const scrollTimerRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    setMessages([]);
    setInput("");
    setResponseId(null);
    setConversationId(null);
    setCarousel(null);
    setPost(null);
  }, [convId]);

  // Add scroll handler
  const handleScroll = () => {
    if (!chatBoxRef.current) return;
    
    // Show button whenever scrolling occurs
    setShowScrollButton(true);
    
    // Clear existing timer
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
    }
    
    // Only hide if not hovering
    if (!isHovering) {
      scrollTimerRef.current = setTimeout(() => {
        setShowScrollButton(false);
      }, 1000);
    }
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    const chatBox = chatBoxRef.current;
    if (chatBox) {
      chatBox.addEventListener('scroll', handleScroll);
      return () => {
        chatBox.removeEventListener('scroll', handleScroll);
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
        }
      };
    }
  }, []);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  function getParsedMessage(m) {
    try {
      const j = JSON.parse(m.content);
      for (let k in j) {
        m[k] = j[k].split("\n");
      }
      if (m.role === "assistant" && m.post) {
        setPost(m.post);
      }
    } catch (err) {
      if (m.type == "function_call") {
        if (m.name == "get_post_examples") {
          m.lines = ["Finding high-engagement post examples..."];
        } else if (m.name == "get_hook_examples") {
          m.lines = ["Finding a good hook based on top-performing examples..."];
        } else {
          m.lines = ["Calling function: " + m.name + "(" + m.arguments + ")"];
        }
      } else if (m.output && m.output.length > 0) {
        m.posts = JSON.parse(m.output);
        m.posts = m.posts.map((p) => {
          return {
            lines: p.split("\n")
          }
        });
        console.log("posts", m.posts);
      } else if (m.content) {
        m.lines = m.content.split("\n");
      }
      // console.log(err);
    }
    return m;
  }

  useEffect(() => {
    if (!convId) return;
    const fetchConversation = async () => {
      try {
        req(`/conversation/${convId}`)
        .then((res) => {
          console.log("conversation", res);
          if (res.carousel) {
            setCarousel(res.carousel);
          }
          if (res.post) {
            setPost(res.post);
          }
          for (let i = res.messages.length - 1; i >= 0; i--) {
            const m = res.messages[i];
            if (m.role === "assistant" && m.response_id) {
              console.log("setting response id", m.response_id);
              setResponseId(m.response_id);
              break;
            }
          }

          // filter out empty assistant messages & system messages
          res.messages = res.messages.filter((m) => {
            return (m.role !== "assistant" || (m.role === "assistant" && m.content?.length > 0)) && (m.role !== "system");
          });

          

          const msges = res.messages.map((m) => getParsedMessage(m));
          setMessages(msges || []);
          setConversationId(convId);
          
        })
        .catch((err) => {
          console.error("Failed to fetch conversation:", err);
        });
      } catch (err) {
        console.error("Failed to fetch conversation:", err);
      }
    };
    fetchConversation();
  }, [convId]);

  const sendMessage = async (text = input) => {
    if (!text.trim()) return;
    const userMsg = { role: "user", lines: text.split("\n") };
    const assistantMsg = { role: "assistant", lines: [] };
    setMessages([...messages, userMsg, assistantMsg]);
    setInput("");
    setIsLoading(true);

    controllerRef.current = new AbortController();
    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      const token = localStorage.getItem("mmb_token");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch("https://live.makemybrand.ai/chat", {
        method: "POST",
        body: JSON.stringify({ user_msg: text, prev_response_id: responseId, conv_id: conversationId }),
        signal: controllerRef.current.signal,
        credentials: "include",
        headers: headers,
      }).catch((err) => {
        console.log(err);
        throw err;
      });
      if (!res.ok || !res.body) throw new Error("Request failed or no stream");


      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let partial = '';
      let msgText = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        partial += decoder.decode(value, { stream: true });

        let lines = partial.split("\n");
        partial = lines.pop(); // hold incomplete line

        for (let line of lines) {
          const msg = JSON.parse(line);
          
          if (msg.type === "response_text") {
            msgText += msg.data;
  
            let postWrapper;
            try {
              postWrapper = JSON.parse(msgText);
            } catch (err) {
            }
            if (postWrapper) {
              if (postWrapper.post) {
                // assistantMsg.post = postWrapper.post;
                // assistantMsg.lines = [];
                setPost(assistantMsg.post);
              }
              for (let k in postWrapper) {
                assistantMsg[k] = postWrapper[k];
              }
            } else {
              assistantMsg.lines = msgText.split("\n");
            }
            setMessages([...messages.slice(0, -1), assistantMsg]);

          } else if (msg.type === "response_id") {
            setResponseId(msg.data);
          } else if (msg.type === "error") {
            msgText += "Sorry, there was an error. Please try again later.";
          } else if (msg.type === "conversation_id") {
            setConversationId(msg.data);
            navigate(`/chat/${msg.data}`);
          }
        }
      }

    } catch (err) {
      console.log(err); 
      setMessages((prev) => [
        ...prev.slice(0, -1),
        { role: "assistant", lines: [`Sorry, there was an error. Please try again later.`] },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const abortStream = () => {
    controllerRef.current?.abort();
    setIsLoading(false);
    setMessages((prev) => [
      ...prev.slice(0, -1),
      { role: "assistant", lines: ["(response aborted)"] },
    ]);
  };

  const handleKeyPress = (e) => {
    if (!e.ctrlKey && !e.shiftKey && e.key === "Enter" && !isLoading) {
      sendMessage();
    }
  };

  const [debounceTimeout, setDebounceTimeout] = useState(null);

  const updatePostOnServerPost = () => {
    console.log("updating post on server post", post);
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    setDebounceTimeout(setTimeout(() => {
      req(`/conversation-metadata/${conversationId}`, 'POST', JSON.stringify({
        post: post,
      }));
    }, 3000));
  }

  const renderMessageContent = (m, isLastMessage) => {
    if (!m) return null;
  
    return (
      <>
        <MessageHeader role={m.role} timestamp={m.ts?.$date} />     
          
          {m.intro && <div>render_lines(m.intro)</div>}
          {m.post && <div>
            {render_lines(m.post)}
          </div>}
  
          {m.posts && m.posts.length > 0 &&
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 10,
              overflowX: 'auto',
              width: '100%',
              '::-webkit-scrollbar': {
                  width: '4px',
                  height: '4px',
              },
              '::-webkit-scrollbar-thumb': {
                  backgroundColor: 'grey',
                  borderRadius: '10px',
              },
              '::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
              },
              '::-webkit-scrollbar-button': {
                  display: 'none',
                  width: 0,
                  height: 0,
              },
              scrollbarWidth: 'thin',
              scrollbarColor: 'grey transparent',
          }}>
            {m.posts.map((post, index) => (
              <div key={index} style={{
                border: "1px solid #efefef",
                borderRadius: 10,
                padding: 10,
                marginTop: 10,
                minWidth: 300,
                minHeight: 100,
                maxHeight: 400,
                overflowY: 'auto',
              }}>
                {render_lines(post.lines)}
              </div>
            ))}
          </div>
          }
          
          {Array.isArray(m.lines) && (m.lines.length === 0 && isLastMessage && isLoading ? (
            <Box sx={{ width: 100, height: 10, borderRadius: 1, background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)", animation: "skeleton-loading 1.5s infinite linear", backgroundSize: "200% 100%" }} />
          ) : (
            <div 
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}>
                {/* split by regex of \n or ' ' */}
                {isLastMessage && Array.isArray(m.lines) && m.lines?.join('\n').split(/(\n|\s)/).map((word, wordIndex) => (
                  word == '\n' ? <div key={wordIndex} style={{marginTop: "2px", width: "100%"}} /> :
                  <motion.span
                    key={wordIndex}
                    style={{ marginRight: "4px" }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.03, delay: wordIndex * 0.03 }}
                  >
                    {word}
                  </motion.span>
                ))}
  
                {!isLastMessage && Array.isArray(m.lines) && m.lines.map((line, lineIndex) => (
                  <div key={lineIndex}>
                    {line}
                  </div>
                ))}
                
            </div>
          ))}
      </>)
  }
  
  return (
    <>
    <ChatNavbar selected="write" />

    <Box display="flex" height="94vh" width="100%">
    
      <Box display="flex" flexDirection="column" mx="auto" p={2} style={{
          width: "30%",
          backgroundColor: "#1C1C1C",
          position: "relative",
          marginRight: "auto",
          marginLeft: "0px",
        }}>

        <Box ref={chatBoxRef} flexGrow={messages?.length === 0 ? 0 : 1} overflow="auto" pr={1} sx={{
              '&::-webkit-scrollbar': {
                  width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                  background: 'grey',
                  borderRadius: '10px',
              },
              overflow: 'auto',
          }} >
          {Array.isArray(messages) && messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                display: "flex",
                justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                marginBottom: "20px",
                width: "90%",
                position: "relative",
              }}
            >
              <Avatar
                src={m.role === "user" ? "/images/avatar.jpg" : "/images/chatbot.jpg"}
                sx={{ width: 30, height: 30, mr: 1 }}
              />
              <Paper 
                sx={{ 
                  p: 1.5, 
                  maxWidth: "100%", 
                  borderRadius: 2, 
                  boxShadow: 0, 
                  backgroundColor: "#1C1C1C",
                  color: "white",
                  border: 'none',
                  padding: 0,
                }}
              >
                {renderMessageContent(m, i === messages.length - 1)}
              </Paper>
            </motion.div>
          ))}
        </Box>

        <AnimatePresence>
          {showScrollButton && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                bottom: '130px',
                left: '45%',
                zIndex: 1000,
              }}
            >
              <IconButton
                onClick={scrollToBottom}
                onMouseEnter={() => {
                  setIsHovering(true);
                  // Clear any existing hide timer
                  if (scrollTimerRef.current) {
                    clearTimeout(scrollTimerRef.current);
                  }
                }}
                onMouseLeave={() => {
                  setIsHovering(false);
                  // Start hide timer
                  if (scrollTimerRef.current) {
                    clearTimeout(scrollTimerRef.current);
                  }
                  scrollTimerRef.current = setTimeout(() => {
                    setShowScrollButton(false);
                  }, 1000);
                }}
                sx={{
                  border: '1px solid white',
                  backgroundColor: '#1C1C1C',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgb(43, 42, 42)',
                  },
                }}
              >
                <ArrowDownwardOutlinedIcon sx={{fontSize: 18}}/>
              </IconButton>
            </motion.div>
          )}
        </AnimatePresence>

        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          maxWidth: '98%',
          width: '98%',
          margin: "0 auto",
          justifyContent: "start",
          flexGrow: messages?.length === 0 ? 1 : 0,
        }}>
          <ChatButtons 
            input={input} 
            setInput={setInput} 
            isLoading={isLoading} 
            handleKeyPress={handleKeyPress} 
            sendMessage={sendMessage} 
            abortStream={abortStream} 
            messages={messages || []} 
          />
          
          {/* {messages.length === 0 && <Suggestions sendMessage={sendMessage} />} */}
        </div>
        
      </Box>

      {post &&
        <Box display="flex" flexDirection="column" flexGrow={1} mx="auto"  style={{
          height: "100%", backgroundColor: "#1C1C1C",
        }}>
            <PostPreview post={post} carousel={carousel} setCarousel={setCarousel} conversationId={conversationId} setPost={setPost} updatePostOnServerPost={updatePostOnServerPost}/>       
        </Box>}
        {!post &&
        <Box display="flex" flexDirection="column" flexGrow={1} mx="auto"  style={{
          height: "100%", backgroundColor: "#1C1C1C",
        }}>
          <div style={{textAlign: "center", width: "100%",height: "100%", backgroundColor: "white",borderRadius: 10}}>
          </div>
        </Box>}
      
    </Box>
    </>
  );
}

function render_lines(lines) {
  if (!lines || !Array.isArray(lines)) return null;
  return <div style={{
    display: "flex",
    gap: 10,
    height: "100%",
    justifyContent: "start",
    flexDirection: "column",
  }}>{lines.map((line, index) => (
    <div key={index} style={{
      width: "100%",
      lineHeight: 1,
    }}>
      {line}
    </div>
  ))}</div>;
}

function ChatButtons({input, setInput, isLoading, handleKeyPress, sendMessage, abortStream, messages}) {
  const inputRef = useRef(null);
  
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  return (
    <Box display="flex" mt={2} mb={2} sx={{
      position: 'relative',
      backgroundColor: '#272725',
      borderRadius: '8px',
      padding: '8px',
      border: '1px solid white',

      '& textarea::-webkit-scrollbar': {
        width: '6px',
      },
      '& textarea::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '& textarea::-webkit-scrollbar-thumb': {
        background: '#666',
        borderRadius: '4px',
      },
      '& textarea::-webkit-scrollbar-thumb:hover': {
        background: '#888',
      },
      '& textarea': {
        scrollbarWidth: 'thin',
        scrollbarColor: 'grey transparent',
      }
    }}>
      <TextareaAutosize
        inputRef={inputRef}
        fullWidth
        placeholder={messages.length == 0 ? "Say hi" : "Type a response"}
        value={input}
        minRows={3}
        maxRows={4}
        style={{
          width: "100%",
          padding: "8px 40px 8px 12px",
          backgroundColor: 'transparent',
          border: 'none',
          color: 'white',
          fontSize: '14px',
          resize: 'none',
          outline: 'none',
          fontFamily: 'inherit',
        }}
        disabled={isLoading}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyPress}
      />
      <IconButton 
        onClick={() => sendMessage()} 
        disabled={isLoading || !input.trim()}
        sx={{
          position: 'absolute',
          right: '25px',
          top: '70%',
          transform: 'translateY(-50%)',
          padding: '4px',
          backgroundColor: 'white',
          '&.Mui-disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
          },
          '&:hover': {
            backgroundColor: 'white',
          }
        }}
      >
        <ArrowUpwardOutlinedIcon sx={{
          fontSize: 18, 
          color: isLoading || !input.trim() ? 'rgba(0, 0, 0, 0.8)' : '#1C1C1C'
        }} />
      </IconButton>
    
    </Box>
  )
}


export function getRecency(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
  const diffMinutes = Math.ceil(diffTime / (1000 * 60));
  const diffSeconds = Math.ceil(diffTime / 1000);
  if (diffSeconds < 60) {
    return `${diffSeconds}s ago`;
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays < 30) {
    return `${diffDays}d ago`;
  }

  const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
  if (diffMonths < 12) {
    return `${diffMonths}m ago`;
  }
  const diffYears = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 365));
  return `${diffYears}y ago`;
}

function Suggestions({sendMessage}) {
  return  (
    <div style={{
      border: "1px solid rgb(241 241 241)",
      borderRadius: 6,
      padding: 10,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      margin: "0 auto",
      justifyContent: "start",
      width: "100%",
    }}>
      {[
        "What is Ghibli Studio Art?",
        "Embracing failure on LinkedIn",
        "Comments Performance Analysis",
        "Post Performance Analysis Request",
      ].map((text, i) => (
          <div
          key={i}
            style={{
              padding: 10,
              cursor: "pointer",
              fontSize: "0.9rem",
              opacity: 0.8,
            }}
            onClick={() => sendMessage(text)}
          >
            {text}
          </div>
      ))}
    </div>);
}


function PostPreview({post, carousel, setCarousel, conversationId, setPost, updatePostOnServerPost}) {
  return <>
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      // border: "1px solid #ccc",  
      padding: 3,
      borderRadius: 10,
      overflow: "auto",
      backgroundColor: "#F7F4FE",
      flexGrow: 1,
      width: "100%",
    }}>
      <LinkedinPostPreview text={post.join('\n')} carousel={carousel} setText={t => {
        setPost(t.split('\n'));
        updatePostOnServerPost();
      }} post={post} setCarousel={setCarousel} conversationId={conversationId} />
    </div>
    
  </>
}

function formatTimestamp(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const time = date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();
  return `${time} on ${month} ${day}, ${year}`;
}

function MessageHeader({ role, timestamp }) {
  const [showTimestamp, setShowTimestamp] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Typography 
      variant="subtitle2" 
      gutterBottom 
      sx={{ 
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        justifyContent: 'space-between',
        width: '100%'
      }}
      onMouseEnter={() => setShowTimestamp(true)}
        onMouseLeave={() => setShowTimestamp(false)}
    >
      <span 
        
      >
        {role === "user" ? "You" : "Assistant"}
        {role === "assistant" && timestamp && showTimestamp && (
          <span 
            style={{
              color: 'grey',
              fontSize: '12px',
              marginLeft: '20px',
            }}
          >
            {formatTimestamp(timestamp)}
          </span>
        )}
      </span>
      {role === "assistant" && (
        <div style={{
          position: 'absolute',
          right: 0,
        }}>
          <IconButton
            size="small"
            onClick={handleClick}
            sx={{ 
              color: 'grey',
              padding: 0,
              transform: 'translateX(40px)',
              '&:hover': {
                color: 'white'
              }
            }}
          >
            <MoreHorizOutlinedIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: '#1e1e1e',
                color: 'white',
                marginTop: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                minWidth: '200px',
              },
              '& .MuiMenuItem-root': {
                fontSize: '14px',
                gap: '8px',
                padding: '8px 16px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }
            }}
          >
            <MenuItem onClick={handleClose} sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              Copy message link
              <InsertLinkOutlinedIcon fontSize='18px' />
            </MenuItem>
            <MenuItem onClick={handleClose} sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              Preview
              <CallMadeOutlinedIcon fontSize='18px' />
            </MenuItem>
            <MenuItem onClick={handleClose} sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              Restore
              <RestoreIcon fontSize='18px' />
            </MenuItem>
          </Menu>
        </div>
      )}
    </Typography>
  );
}
